import styled from '@emotion/styled';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import { Topics } from 'src/modules/programs/components/Topics';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import * as S from 'src/modules/programs/stepComponents/styles';
import {
    fetchSeveralProgramSurveys,
    submitProgramSurvey,
} from 'src/modules/programs/surveys/actions/surveysActions';
import { ProgramSurvey } from 'src/modules/programs/surveys/components/ProgramSurvey';
import { QuestionHeader, QuestionWrapper } from 'src/modules/programs/surveys/components/styles';
import { SurveyOptionsQuestionAnswer } from 'src/modules/programs/surveys/models/surveys';
import {
    getProgramSurveyAnswers,
    getProgramSurveySingleQuestionAnswer,
    isProgramSurveyAnswered,
} from 'src/modules/programs/surveys/selectors/surveysSelectors';

import { CBTProgramWeekSixSurvey } from '../models/weekSix';

const CurrentStepSurveyWrapper = styled('div')({
    padding: '2rem 0rem',
});

const prevStepQuestionId = 69;
const prevStepSurveyId = CBTProgramWeekSixSurvey.WhatAreYourBehaviors;
const currentStepSurveyId = CBTProgramWeekSixSurvey.Consequences;

interface StateProps {
    currentStepAnswers: SurveyOptionsQuestionAnswer[] | undefined;
    prevStepAnswers: SurveyOptionsQuestionAnswer | undefined;
    isSurveyAnswered: boolean;
}

interface ActionProps {
    fetchSeveralProgramSurveys: typeof fetchSeveralProgramSurveys;
    submitProgramSurvey: typeof submitProgramSurvey;
}

interface Props extends StateProps, ActionProps, StepButtonsProps, WithTranslation {
    programId: ProgramEnum;
    noShadow?: boolean;
    noPadding?: boolean;
}

class ConsequencesStepContainer extends React.Component<Props> {
    public componentDidMount(): void {
        const { programId } = this.props;

        this.props.fetchSeveralProgramSurveys({
            programId,
            surveysIds: [
                CBTProgramWeekSixSurvey.WhatAreYourBehaviors,
                CBTProgramWeekSixSurvey.Consequences,
            ],
        });
    }

    private handleSubmit = () => {
        const { programId, currentStepAnswers, goToNextStep } = this.props;

        if (!currentStepAnswers) {
            return;
        }

        this.props.submitProgramSurvey({
            programId,
            surveyId: CBTProgramWeekSixSurvey.Consequences,
            answers: currentStepAnswers,
        });

        goToNextStep();
    };

    public render(): JSX.Element | null {
        const {
            t,
            prevStepAnswers,
            noShadow,
            noPadding,
            goToPreviousStep,
            isSurveyAnswered,
        } = this.props;

        const prevStepAnswersTopics = prevStepAnswers && prevStepAnswers.data.options;

        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading>{t('week-6.consequences.title')}</S.StepHeading>
                        <S.StepDescription small>
                            {t('week-6.consequences.description')}
                        </S.StepDescription>
                    </S.ContentColumn>
                    <QuestionWrapper noShadow={noShadow} noPadding={noPadding}>
                        <QuestionHeader large>
                            {t('week-6.consequences.questionHeader')}
                        </QuestionHeader>
                        {prevStepAnswersTopics && (
                            <Topics displayOnly topics={prevStepAnswersTopics} />
                        )}
                        <CurrentStepSurveyWrapper>
                            <ProgramSurvey
                                wrapped
                                wrapQuestions
                                programId={this.props.programId}
                                surveyId={CBTProgramWeekSixSurvey.Consequences}
                                noFetch
                            />
                        </CurrentStepSurveyWrapper>
                    </QuestionWrapper>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!isSurveyAnswered}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState, { programId }: Props): StateProps => ({
    prevStepAnswers: getProgramSurveySingleQuestionAnswer(
        state,
        programId,
        prevStepSurveyId,
        prevStepQuestionId,
    ) as SurveyOptionsQuestionAnswer | undefined,
    currentStepAnswers: getProgramSurveyAnswers(state, programId, currentStepSurveyId) as
        | SurveyOptionsQuestionAnswer[]
        | undefined,
    isSurveyAnswered: isProgramSurveyAnswered(state, programId, currentStepSurveyId),
});

export const ConsequencesStep = withTranslation('cbt')(
    connect<StateProps>(mapStateToProps, {
        fetchSeveralProgramSurveys,
        submitProgramSurvey,
    })(ConsequencesStepContainer),
);
